import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, Code, Coins, Copy, ExternalLink, Layers, Plus, Receipt, RefreshCw, ShieldAlert, ShieldCheck, Sparkles, Tag, TrendingDown, TrendingUp,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { billingApi, type SyncSubscriptionResult } from '../../services/billingApi';
import { toastService } from '../../services/toastService';
import {
  getPricing, getTenantPricing, getTenantCreditsSummary,
  getTenantTransactionHistory, grantCredits, updateTenantPricing,
} from '../../services/aiUsageApi';
import { Card, CardHeader, ConfirmDialog, Note } from './financeUi';
import PlanAssignmentBuilder from './PlanAssignmentBuilder';
import {
  money, shortDate, shortDateTime, freeQuotaUsage, financeTotals, type LocationRow,
} from './financeFormat';
import type { PricePlan, Tenant } from '../../types';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE → PLAN
 *
 * Spec §9: "current plan · packages · quota".
 *
 * This is where the money half of the old Overview tab landed — the subscription
 * card, the cancellation request, the Stripe ids and the AI credit wallet —
 * plus the free-location quota that used to sit on the Locations tab. Identity,
 * contacts and status stayed in Overview.
 *
 * "Change plan" and cancel/reactivate were DELETED 2026-09-07. They drove
 * `tenant.stripeSubscriptionId` — the single tenant-level subscription of the
 * pre-per-location model — which billing no longer uses and the migration
 * clears. A plan changes in the Plan assignment card, per location.
 */

interface Props {
  tenantId: string;
  tenant: Tenant | null;
  locations: LocationRow[];
  /** Only for NAMING the plan the tenant is on — nothing here changes it. */
  prices: PricePlan[];
  /** Catalog SKUs and the shadow-mode entitlement comparison from billing-service. */
  skus: any[];
  entitlements: any | null;
  /** Reload the tenant record (and its billing events) from the server. */
  onReload: () => Promise<void> | void;
  notify: (message: string) => void;
}

interface CreditWallet {
  balance: number;
  totalLoaded: number;
  totalSpent: number;
  chargeEnabled: boolean;
  unitPrice: number;
  transactions: Array<{
    transactionId: string;
    type: string;
    amount: number;
    balanceAfter: number;
    createdAt: number;
    description?: string;
  }>;
}

/** pos-agents ledger row -> the wallet's transaction shape. */
const mapLedgerRow = (row: any) => {
  const typeMap: Record<string, string> = {
    CONSUME: 'spend', ADMIN_GRANT: 'admin_load', PURCHASE: 'load', REFUND: 'refund', ADMIN_DEDUCT: 'deduct',
  };
  return {
    transactionId: String(row.sk),
    type: typeMap[row.type] || String(row.type || '').toLowerCase(),
    amount: Number(row.creditsAmount || 0),
    balanceAfter: Number(row.balanceAfter || 0),
    createdAt: Date.parse(row.createdAt) || Date.now(),
    description: row.operation,
  };
};

const ENFORCE_STYLE: Record<string, string> = {
  suspend_all: 'bg-red-100 text-red-700',
  suspend_feature: 'bg-amber-100 text-amber-700',
  collections_only: 'bg-sky-100 text-sky-700',
};

const PlanPanel: React.FC<Props> = ({
  tenantId, tenant, locations, prices, skus, entitlements, onReload, notify,
}) => {
  const [confirm, setConfirm] = useState<'deny' | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const [processingCancellation, setProcessingCancellation] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [quota, setQuota] = useState<number>(1);
  const [savingQuota, setSavingQuota] = useState(false);

  const [wallet, setWallet] = useState<CreditWallet | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [togglingCharge, setTogglingCharge] = useState(false);

  const [syncingBilling, setSyncingBilling] = useState(false);
  const [lastSync, setLastSync] = useState<SyncSubscriptionResult | null>(null);
  const [syncRunError, setSyncRunError] = useState<string | null>(null);

  useEffect(() => { setQuota((tenant as any)?.freeLocationQuota ?? 1); }, [tenant]);
  // Route is /tenant/:tenantId with no `key` on either the route or this
  // component, so React Router does not remount PlanPanel on a param-only
  // navigation. Without this, a leftover `lastSync`/`syncRunError` from
  // tenant A would render against tenant B's Finance tab if any tenant
  // switcher ever navigates between profiles without a full page load.
  useEffect(() => {
    setLastSync(null);
    setSyncRunError(null);
    setSyncingBilling(false);
  }, [tenantId]);

  const loadWallet = async () => {
    setWalletLoading(true);
    try {
      const [summary, pricing, history, tenantPricing] = await Promise.all([
        getTenantCreditsSummary(tenantId, { preset: '30d' }),
        getPricing().catch(() => null),
        getTenantTransactionHistory(tenantId, { limit: 10 }).catch(() => ({ items: [] as any[] })),
        getTenantPricing(tenantId).catch(() => null),
      ]);
      const apis = tenantPricing?.resolved?.apis;
      setWallet({
        balance: summary.balance ?? 0,
        totalLoaded: summary.lifetime?.totalLoaded ?? 0,
        totalSpent: summary.lifetime?.totalConsumed ?? 0,
        chargeEnabled: apis ? Object.values(apis).some((a: any) => !a.isFree) : true,
        unitPrice: pricing?.creditUnitPriceUsd ?? 0.01,
        transactions: (history.items || []).map(mapLedgerRow),
      });
    } catch {
      // The wallet lives in a different service. Say so rather than showing a
      // zero balance that reads as fact.
      setWallet(null);
    } finally {
      setWalletLoading(false);
    }
  };
  useEffect(() => { loadWallet(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tenantId]);

  // -- plan ---------------------------------------------------------------
  const currentPlan = useMemo(() => prices.find((p) => p.id === tenant?.priceId), [prices, tenant?.priceId]);
  const planName = currentPlan?.nickname || currentPlan?.productName || 'No Plan';
  const planCents = currentPlan?.unitAmount ?? tenant?.subscriptionCost ?? 0;
  /**
   * Reconciles Stripe with this tenant's plan + packages (design §7). Not a
   * read — the first sync of a tenant with pre-existing lines can issue a
   * metadata-only Stripe write, so this only ever runs from an explicit
   * action: this button, or right after a package assign/remove elsewhere in
   * the app. It never runs automatically on page load.
   *
   * The endpoint is idempotent (diffs desired against actual), so pressing
   * this twice — including as a retry after a failed sync — is always safe.
   */
  const runBillingSync = async () => {
    if (!tenantId) return;
    setSyncingBilling(true);
    setSyncRunError(null);
    try {
      const result = await billingApi.syncSubscription(tenantId);
      setLastSync(result);
      if (result.skipped === 'no-plan-assignment') {
        toastService.info('This tenant is on legacy flat pricing — create a plan assignment to bill per package.');
      } else if (result.errors.length) {
        toastService.warning('Billing did not update. The tenant keeps its packages — retry when ready.', 10000);
      } else {
        const changed = result.added + result.removed + result.repriced;
        toastService.success(changed > 0
          ? `Billing synced — ${result.added} added, ${result.removed} removed${result.repriced ? `, ${result.repriced} repriced` : ''}.`
          : "Billing already matches this tenant's plan.");
      }
      await onReload();
    } catch (err: any) {
      setSyncRunError(err?.message || 'Could not reach billing service');
      toastService.error(err?.message || 'Failed to sync billing');
    } finally {
      setSyncingBilling(false);
    }
  };

  const approveCancellation = async () => {
    setProcessingCancellation(true);
    try {
      await api.superAdmin.approveCancellationRequest(tenantId);
      notify('Cancellation request approved. Subscription will cancel at period end.');
      await onReload();
    } catch (err: any) {
      notify(err?.message || 'Failed to approve cancellation');
    } finally {
      setProcessingCancellation(false);
    }
  };

  const denyCancellation = async () => {
    setProcessingCancellation(true);
    try {
      await api.superAdmin.denyCancellationRequest(tenantId, denyReason || 'Request denied by administrator');
      setConfirm(null);
      setDenyReason('');
      notify('Cancellation request denied');
      await onReload();
    } catch (err: any) {
      notify(err?.message || 'Failed to deny cancellation');
    } finally {
      setProcessingCancellation(false);
    }
  };

  const previewUpcoming = async () => {
    if (!tenant?.stripeSubscriptionId) return;
    try {
      const invoice = await api.superAdmin.upcomingInvoice(tenant.stripeSubscriptionId);
      notify(`Next invoice: ${money(invoice?.amount_due, invoice?.currency || 'usd')}`);
    } catch (err: any) {
      notify(err?.message || 'Failed to preview');
    }
  };

  const saveQuota = async () => {
    setSavingQuota(true);
    try {
      await api.superAdmin.updateTenantFields(tenantId, { freeLocationQuota: quota });
      notify('Free location quota updated');
      await onReload();
    } catch (err: any) {
      notify('Failed to update quota: ' + (err?.message || 'unknown error'));
    } finally {
      setSavingQuota(false);
    }
  };

  const grant = async () => {
    const credits = Math.round(parseFloat(grantAmount));
    if (!Number.isFinite(credits) || credits <= 0) {
      notify('Enter a positive number of credits to load');
      return;
    }
    setGranting(true);
    try {
      const result = await grantCredits(tenantId, { credits, reason: grantNote || 'Admin credit load' });
      setGrantAmount('');
      setGrantNote('');
      notify(`Loaded ${credits.toLocaleString()} credits (balance ${Number(result.balance).toLocaleString()})`);
      await loadWallet();
    } catch (err: any) {
      notify(err?.message || 'Failed to load credits');
    } finally {
      setGranting(false);
    }
  };

  const toggleCharging = async (enabled: boolean) => {
    setTogglingCharge(true);
    try {
      if (enabled) await updateTenantPricing(tenantId, { resetToGlobal: true });
      else await updateTenantPricing(tenantId, {
        apis: { anthropic: { free: true }, google_vertex: { free: true }, google_cse: { free: true } },
      });
      setWallet((prev) => (prev ? { ...prev, chargeEnabled: enabled } : prev));
      notify(`Charging ${enabled ? 'enabled' : 'disabled'} for this tenant`);
    } catch (err: any) {
      notify(err?.message || 'Failed to update charging status');
    } finally {
      setTogglingCharge(false);
    }
  };

  const copy = (value: string, id: string) => {
    navigator.clipboard.writeText(value);
    setCopiedId(id);
    setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
  };

  // -- packages -----------------------------------------------------------
  const skuById = useMemo(() => Object.fromEntries(skus.map((s: any) => [s.skuId, s])), [skus]);
  const entRows: any[] = entitlements?.entitlements ?? [];
  const tenantScoped = useMemo(
    () => entRows.map((e) => skuById[e.skuId]).filter((s: any) => s && s.scope === 'tenant'),
    [entRows, skuById],
  );
  const locationScoped = useMemo(
    () => entRows.map((e) => skuById[e.skuId]).filter((s: any) => s && s.scope === 'location'),
    [entRows, skuById],
  );
  const unpriced = [...tenantScoped, ...locationScoped].filter((s: any) => !s.amountCents).length;

  const quotaUse = freeQuotaUsage(locations, (tenant as any)?.freeLocationQuota);
  const active = tenant?.subscriptionStatus === 'active';

  /**
   * The base plan plus the per-location subscriptions, exactly as the Software
   * KPI counts them — the lines a plan assignment does NOT touch.
   *
   * `subscriptionSync` maintains one managed software line per location BESIDE
   * the base plan line and never names a line it did not stamp, so this figure
   * is what stays the same when a plan is activated. The builder shows it next
   * to the composed amount rather than folding it in, so the composed net is
   * never mistaken for the tenant's whole bill.
   *
   * Read off `financeTotals` — the SAME function the Software KPI tile calls
   * (`TenantFinancePanels.tsx`) — with `packageCents: 0`, since entitlement
   * packages ARE touched by a plan assignment and do not belong in "does NOT
   * touch". Two hand-written reductions over `locations` computing the same
   * money figure is one too many to keep in step.
   */
  const baseSubscriptionCents = useMemo(
    () => financeTotals({ basePlanCents: planCents, locations, agreements: [], packageCents: 0 }).softwareCents,
    [planCents, locations],
  );

  return (
    <div className="space-y-4 text-stone-800">
      {/* Billing sync failure — persistent, so it survives after the toast that
          reported it disappears. This is the whole point of billingSyncPending:
          the entitlement change already took effect; billing has not caught up. */}
      {tenant?.billingSyncPending && (
        <div className="bg-red-50 border border-red-200 text-red-900 rounded-2xl p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <ShieldAlert className="text-red-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1 min-w-[16rem]">
              {/* Two different problems share this banner, and they point at
                  DIFFERENT systems. `billingRecordGap` means the subscriptions
                  are live and charging the right amount — what is missing is the
                  internal record of how that price was arrived at, without which
                  no later package change can be priced. Telling an operator
                  "the last attempt to update Stripe failed" there would send
                  them to look at a system that is working. */}
              <h4 className="font-semibold mb-1">
                {tenant.billingRecordGap ? 'Billing is correct, but unrecorded' : 'Billing did not update'}
              </h4>
              <p className="text-sm text-red-800">
                {tenant.billingRecordGap ? (
                  <>
                    This tenant's subscriptions were created and are billing the right amount, but we could not record
                    the plan behind that price, so no package change can be priced until it is set
                    up{tenant.billingSyncError ? `: ${tenant.billingSyncError}` : '.'}
                  </>
                ) : (
                  <>
                    This tenant's packages were saved and are already in effect, but the last attempt to update Stripe
                    failed{tenant.billingSyncError ? `: ${tenant.billingSyncError}` : '.'}
                  </>
                )}
              </p>
              {tenant.billingSyncAt && (
                <p className="text-xs text-red-600/80 mt-1">Last attempt {shortDateTime(tenant.billingSyncAt)}</p>
              )}
            </div>
            <button
              onClick={runBillingSync}
              disabled={syncingBilling}
              className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50 min-h-[44px] whitespace-nowrap inline-flex items-center gap-1.5"
            >
              {syncingBilling ? <RubyGemMark size={14} drawing color="currentColor" /> : <RefreshCw size={14} />}
              {syncingBilling ? 'Retrying…' : 'Retry sync'}
            </button>
          </div>
        </div>
      )}

      {/* Current plan ------------------------------------------------------ */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className={`rounded-2xl p-4 border ${
          active ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'
        }`}>
          <p className={`text-[10px] uppercase tracking-wider mb-1 ${active ? 'text-emerald-600' : 'text-red-600'}`}>
            Current plan
          </p>
          <p className="text-xl font-bold text-stone-900">{planName}</p>
          <p className="text-stone-600 text-sm">{money(planCents)}/month</p>
          {tenant?.dealCode && (
            <p className="text-xs text-stone-500 mt-1.5 inline-flex items-center gap-1">
              <Tag size={11} /> Deal {tenant.dealCode}
            </p>
          )}
        </div>
        <div className="bg-white text-stone-800 rounded-2xl p-4 border border-stone-200/70 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-stone-500 text-sm">Status</span>
            <span className={`text-sm font-semibold capitalize ${active ? 'text-emerald-600' : 'text-red-600'}`}>
              {tenant?.cancelAtPeriodEnd ? 'Cancelling' : (tenant?.subscriptionStatus || '—')}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-stone-500 text-sm">Next payment</span>
            <span className="text-stone-800 font-medium text-sm">{shortDate(tenant?.currentPeriodEnd)}</span>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={previewUpcoming}
              disabled={!tenant?.stripeSubscriptionId}
              className="flex-1 px-3 py-2 rounded-xl border border-stone-300 text-stone-700 text-xs font-semibold hover:bg-stone-50 disabled:opacity-40 min-h-[40px] inline-flex items-center justify-center gap-1.5"
            >
              <Receipt size={13} /> Preview invoice
            </button>
            <a
              href={tenant?.stripeSubscriptionId ? `https://dashboard.stripe.com/subscriptions/${tenant.stripeSubscriptionId}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 px-3 py-2 rounded-xl border border-stone-300 text-stone-700 text-xs font-semibold hover:bg-stone-50 min-h-[40px] inline-flex items-center justify-center gap-1.5 ${
                tenant?.stripeSubscriptionId ? '' : 'opacity-40 pointer-events-none'
              }`}
            >
              Open Stripe <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>

      {/* Cancellation request ---------------------------------------------- */}
      {tenant?.cancellationRequestStatus === 'pending' && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Cancellation request pending</h4>
              {tenant.cancellationRequestReason && (
                <p className="text-sm italic mb-1">"{tenant.cancellationRequestReason}"</p>
              )}
              <p className="text-xs text-amber-700 mb-3">
                Requested by {tenant.cancellationRequestedBy || 'Unknown'} on {shortDate(tenant.cancellationRequestedAt)}
              </p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={approveCancellation}
                  disabled={processingCancellation}
                  className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 disabled:opacity-50 min-h-[40px]"
                >
                  {processingCancellation ? 'Processing…' : 'Approve cancellation'}
                </button>
                <button
                  onClick={() => setConfirm('deny')}
                  disabled={processingCancellation}
                  className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 disabled:opacity-50 min-h-[40px]"
                >
                  Deny request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/*
        DELETED 2026-09-07 — "Change plan" (a Stripe price dropdown + Apply) and
        the cancel/reactivate strip beneath it.

        Both drove `tenant.stripeSubscriptionId`, the ONE tenant-level
        subscription of the pre-per-location billing model. That model is gone:
        a tenant is billed per LOCATION now, each store carrying its own
        subscription, and `/onboarding/complete` neither writes nor reads that
        field — the migration clears it. So "Apply" repriced a subscription
        nothing is billed on, and "Cancel" cancelled it.

        Deliberately removed rather than disabled or hidden, on the user's
        instruction: a control left in the file is a control some future reader
        wires back up. Plan changes belong to the Plan assignment card below,
        which composes packages per location.
      */}

      {/* Plan assignment ------------------------------------------------------
          The composed price: which packages, at which discount, and the net that
          becomes the managed software line. Everything below this card describes
          what is live TODAY; this card is where it changes. */}
      <PlanAssignmentBuilder
        tenantId={tenantId}
        skus={skus}
        locations={locations}
        baseSubscriptionCents={baseSubscriptionCents}
        onActivated={onReload}
      />

      {/* Packages ----------------------------------------------------------- */}
      <Card>
        <CardHeader
          icon={<Layers size={15} />}
          title="Packages"
          hint={`${tenantScoped.length} business-wide · ${locationScoped.length} per-store`}
          actions={
            <button
              onClick={runBillingSync}
              disabled={syncingBilling}
              title="Reconciles this tenant's Stripe subscription with its plan and packages. Not read-only — the first run for a tenant can write metadata to Stripe, though no money moves."
              className="px-3.5 py-2 bg-stone-900 text-white rounded-xl text-xs font-semibold hover:bg-black disabled:opacity-50 min-h-[40px] inline-flex items-center gap-1.5"
            >
              {syncingBilling ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />}
              {syncingBilling ? 'Syncing…' : 'Sync billing'}
            </button>
          }
        />
        <div className="p-4 space-y-3">
          {/* Result of the last manual sync run from the button above. Three
              distinct states, styled so a skip never reads as a failure. */}
          {syncRunError && (
            <Note tone="danger">
              <strong>Sync billing could not run.</strong> {syncRunError} The tenant's packages are unaffected —
              retry when the billing service is reachable.
            </Note>
          )}
          {!syncRunError && lastSync?.skipped === 'no-plan-assignment' && (
            <Note tone="info">
              <strong>This tenant is on legacy flat pricing.</strong> There is no plan assignment yet, so
              per-package billing does not apply — nothing was changed. Create a plan assignment to bill this
              tenant per package.
            </Note>
          )}
          {!syncRunError && lastSync && !lastSync.skipped && lastSync.errors.length > 0 && (
            <Note tone="danger">
              <strong>Billing did not update.</strong> The tenant already has these packages — that is unaffected.
              {' '}{lastSync.errors[0]?.reason}
              {lastSync.errors.length > 1 ? ` (and ${lastSync.errors.length - 1} more problem${lastSync.errors.length - 1 === 1 ? '' : 's'})` : ''}
            </Note>
          )}
          {!syncRunError && lastSync && !lastSync.skipped && lastSync.errors.length === 0 && (
            <Note tone="success">
              <strong>Billing synced.</strong> {lastSync.added} added · {lastSync.removed} removed
              {lastSync.repriced ? ` · ${lastSync.repriced} repriced` : ''} · {lastSync.unchanged} unchanged.
            </Note>
          )}

          {entitlements && (
            <div className={`rounded-xl px-3.5 py-2.5 text-xs flex items-start gap-2 border ${
              entitlements.shadow?.matches
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}>
              {entitlements.shadow?.matches
                ? <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                : <ShieldAlert size={14} className="mt-0.5 shrink-0" />}
              <span>
                <strong>{entitlements.shadow?.matches ? 'Entitlements reconcile.' : 'Entitlement mismatch.'}</strong>{' '}
                {entitlements.shadow?.live?.length ?? 0} live packages vs{' '}
                {entitlements.shadow?.computed?.length ?? 0} computed from the catalog.
                {entitlements.shadow?.packagesWithNoSku?.length > 0 && (
                  <> {entitlements.shadow.packagesWithNoSku.length} package(s) have no SKU and cannot be priced.</>
                )}
              </span>
            </div>
          )}

          {entRows.length === 0 ? (
            <p className="text-sm text-stone-500 py-2">
              No catalog entitlements resolved for this tenant. Packages are assigned on the{' '}
              <strong className="text-stone-700">Packages</strong> tab; they appear here once their SKU exists in the
              billing catalog.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-stone-800">
                <thead className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Package</th>
                    <th className="px-3 py-2 text-left font-semibold">SKU</th>
                    <th className="px-3 py-2 text-left font-semibold">Scope</th>
                    <th className="px-3 py-2 text-left font-semibold">On non-payment</th>
                    <th className="px-3 py-2 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {[...tenantScoped, ...locationScoped].map((s: any) => (
                    <tr key={s.skuId}>
                      <td className="px-3 py-2 text-stone-800">{s.name}</td>
                      <td className="px-3 py-2 text-xs text-stone-500 font-mono">{s.skuId}</td>
                      <td className="px-3 py-2 text-xs text-stone-600">
                        {s.scope === 'tenant' ? 'Business-wide' : 'Per store'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[11px] ${ENFORCE_STYLE[s.enforcement] || 'bg-stone-100 text-stone-700'}`}>
                          {String(s.enforcement || '—').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-stone-800">
                        {s.amountCents ? money(s.amountCents) : <span className="text-stone-400 text-xs">not priced</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {unpriced > 0 && (
            <Note tone="warn">
              <strong>{unpriced} package(s) have no price.</strong> They bill nothing today. Set a price on the SKU
              in the Finance Hub catalog.
            </Note>
          )}
        </div>
      </Card>

      {/* Free location quota ------------------------------------------------ */}
      <Card>
        <CardHeader icon={<Sparkles size={15} />} title="Free location quota" hint="locations included with the deal" />
        <div className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm">
            <p className="text-stone-700">
              <strong className="tabular-nums">{quotaUse.label}</strong>
            </p>
            <p className="text-xs text-stone-500 mt-0.5">
              Counted against locations that are free <em>today</em>, not the order they were created in.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={quota}
              onChange={(e) => setQuota(Math.max(0, parseInt(e.target.value, 10) || 0))}
              className="w-20 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5 text-stone-800 text-center text-sm min-h-[44px]"
            />
            <button
              onClick={saveQuota}
              disabled={savingQuota || quota === ((tenant as any)?.freeLocationQuota ?? 1)}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold disabled:opacity-40 min-h-[44px]"
            >
              {savingQuota ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        {quotaUse.over && (
          <div className="px-4 pb-4">
            <Note tone="warn">
              {quotaUse.used} locations are free but the deal allows {quotaUse.quota}. Nothing is broken — the extra
              stores simply bill nothing. Raise the quota, or charge for one of them from its billing page.
            </Note>
          </div>
        )}
      </Card>

      {/* Stripe ids ---------------------------------------------------------- */}
      <Card>
        <CardHeader icon={<Code size={15} />} title="Stripe" hint="one Customer per tenant" />
        <div className="p-4 grid md:grid-cols-2 gap-3">
          <IdRow
            label="Customer ID"
            value={tenant?.stripeCustomerId}
            link={`https://dashboard.stripe.com/customers/${tenant?.stripeCustomerId}`}
            copied={copiedId === 'customer'}
            onCopy={() => copy(tenant?.stripeCustomerId || '', 'customer')}
          />
          <IdRow
            label="Subscription ID"
            value={tenant?.stripeSubscriptionId}
            link={`https://dashboard.stripe.com/subscriptions/${tenant?.stripeSubscriptionId}`}
            copied={copiedId === 'subscription'}
            onCopy={() => copy(tenant?.stripeSubscriptionId || '', 'subscription')}
          />
        </div>
      </Card>

      {/* AI credits ---------------------------------------------------------- */}
      <Card>
        <CardHeader
          icon={<Coins size={15} />}
          title="AI credits"
          hint="Ruby Tokens · pos-agents ledger"
          actions={
            wallet && (
              <button
                onClick={() => toggleCharging(!wallet.chargeEnabled)}
                disabled={togglingCharge}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  wallet.chargeEnabled ? 'bg-emerald-500' : 'bg-stone-300'
                } ${togglingCharge ? 'opacity-50' : ''}`}
                title={wallet.chargeEnabled ? 'Charging enabled — click to make AI free' : 'Free tier — click to charge'}
              >
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                  wallet.chargeEnabled ? 'translate-x-7' : ''
                }`} />
              </button>
            )
          }
        />
        {walletLoading ? (
          <RubyLoader variant="section" />
        ) : !wallet ? (
          <div className="p-4">
            <Note tone="warn">
              The pos-agents credit ledger is unreachable, so no balance is shown. That is not the same as a zero
              balance — nothing has been read.
            </Note>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-amber-600">Balance</p>
                <p className="text-xl font-bold tabular-nums text-stone-900">
                  {wallet.balance.toLocaleString()} <span className="text-xs font-medium text-stone-500">cr</span>
                </p>
                <p className="text-[11px] text-stone-500">≈ ${(wallet.balance * wallet.unitPrice).toFixed(2)}</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 text-stone-800 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1">
                  <TrendingUp size={11} className="text-emerald-500" /> Loaded
                </p>
                <p className="text-xl font-bold tabular-nums text-stone-800">{wallet.totalLoaded.toLocaleString()}</p>
                <p className="text-[11px] text-stone-500">≈ ${(wallet.totalLoaded * wallet.unitPrice).toFixed(2)}</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 text-stone-800 rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-wider text-stone-500 flex items-center gap-1">
                  <TrendingDown size={11} className="text-rose-500" /> Spent
                </p>
                <p className="text-xl font-bold tabular-nums text-stone-800">{wallet.totalSpent.toLocaleString()}</p>
                <p className="text-[11px] text-stone-500">≈ ${(wallet.totalSpent * wallet.unitPrice).toFixed(2)}</p>
              </div>
            </div>

            <div className="bg-stone-50 border border-stone-200 text-stone-800 rounded-xl p-3">
              <p className="text-stone-700 font-medium text-sm mb-2 flex items-center gap-1.5">
                <Plus size={14} className="text-stone-500" /> Load credits
              </p>
              <p className="text-stone-500 text-xs mb-2.5">
                Granted free by super admin — no charge to the tenant.
                {grantAmount && Number(grantAmount) > 0
                  ? ` ≈ $${(Math.round(parseFloat(grantAmount) || 0) * wallet.unitPrice).toFixed(2)} at the current rate.`
                  : ''}
              </p>
              <div className="flex gap-2 flex-wrap">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  placeholder="Credits (e.g. 100)"
                  className="flex-1 min-w-[9rem] px-3 py-2.5 bg-white border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 min-h-[44px]"
                />
                <input
                  type="text"
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Note (optional)"
                  className="flex-1 min-w-[9rem] px-3 py-2.5 bg-white border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 min-h-[44px]"
                />
                <button
                  onClick={grant}
                  disabled={granting || !grantAmount}
                  className="px-5 py-2.5 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 disabled:opacity-40 min-h-[44px] inline-flex items-center gap-2"
                >
                  {granting ? <RubyGemMark size={15} drawing color="currentColor" /> : <Plus size={15} />} Load
                </button>
              </div>
            </div>

            {wallet.transactions.length > 0 && (
              <div className="rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-3 py-2 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wide text-stone-500">
                  Recent credit movements
                </div>
                <div className="divide-y divide-stone-100 max-h-[220px] overflow-y-auto">
                  {wallet.transactions.map((t) => (
                    <div key={t.transactionId} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <p className="text-stone-700 truncate">
                          {t.type === 'admin_load' ? 'Admin credit (free)'
                            : t.type === 'spend' ? (t.description || 'AI generation')
                              : t.type === 'refund' ? 'Credit refund'
                                : t.type.replace(/_/g, ' ')}
                        </p>
                        <p className="text-stone-400 text-xs">{shortDateTime(t.createdAt)}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-semibold tabular-nums ${t.type === 'spend' ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {t.type === 'spend' ? '−' : '+'}{Math.abs(t.amount).toLocaleString()} cr
                        </p>
                        <p className="text-stone-400 text-xs tabular-nums">Bal {t.balanceAfter.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Dialogs -------------------------------------------------------------- */}
      <ConfirmDialog
        open={confirm === 'deny'}
        title="Deny cancellation request?"
        confirmLabel="Deny request"
        busy={processingCancellation}
        onConfirm={denyCancellation}
        onClose={() => { setConfirm(null); setDenyReason(''); }}
      >
        <p>The tenant is notified that their cancellation request was denied.</p>
        <label className="block">
          <span className="block text-xs font-semibold text-stone-700 mb-1">Reason (optional)</span>
          <textarea
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            rows={3}
            placeholder="Why the request was denied…"
            className="w-full px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 resize-none"
          />
        </label>
      </ConfirmDialog>
    </div>
  );
};

const IdRow: React.FC<{
  label: string; value?: string; link: string; copied: boolean; onCopy: () => void;
}> = ({ label, value, link, copied, onCopy }) => (
  <div className="bg-stone-50 text-stone-800 rounded-xl p-3 border border-stone-200">
    <p className="text-stone-500 text-[10px] uppercase tracking-wider mb-1">{label}</p>
    <div className="flex items-center justify-between gap-2">
      <a
        href={value ? link : undefined}
        target="_blank"
        rel="noopener noreferrer"
        className={`font-mono text-sm text-stone-600 hover:text-stone-900 truncate inline-flex items-center gap-1.5 ${
          value ? '' : 'pointer-events-none'
        }`}
      >
        {value || 'Not set'} {value && <ExternalLink size={12} />}
      </a>
      {value && (
        <button onClick={onCopy} className="text-stone-400 hover:text-stone-700 shrink-0 p-1" aria-label={`Copy ${label}`}>
          {copied ? <Check size={15} className="text-emerald-500" /> : <Copy size={15} />}
        </button>
      )}
    </div>
  </div>
);

export default PlanPanel;
